import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { formatBytes } from "@/lib/upload";
import { Paperclip, X, FileText, ImageIcon, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const ACCEPT = ".pdf,.docx,.txt,.png,.jpg,.jpeg,application/pdf,text/plain,image/png,image/jpeg,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const EXTS = ["pdf", "docx", "txt", "png", "jpg", "jpeg"];

export function useAttachmentMeta() {
  const [meta, setMeta] = useState({ max_mb: 10, label: "PDF, DOCX, TXT, PNG or JPG · up to 10 MB · one file per question", warning: "Do not upload Social Security numbers, bank information, passwords, complete medical records or unnecessary sensitive information." });
  useEffect(() => { api.get("/chat/attachments/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  return meta;
}

export function validateAttachment(file, maxMb) {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (!EXTS.includes(ext)) return `Unsupported file type .${ext || "?"}. Allowed: PDF, DOCX, TXT, PNG, JPG.`;
  if (file.size > maxMb * 1024 * 1024) return `File is ${formatBytes(file.size)} — the limit is ${maxMb} MB.`;
  if (file.size === 0) return "The file is empty.";
  return null;
}

// Paperclip trigger (hidden native input; mobile browsers offer camera/gallery for image types)
export function AttachButton({ disabled, onPick, maxMb }) {
  const inputRef = useRef(null);
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const err = validateAttachment(f, maxMb);
    if (err) { toast.error(err); return; }
    onPick(f);
  };
  return (
    <>
      <input ref={inputRef} type="file" accept={ACCEPT} className="sr-only" tabIndex={-1} aria-hidden="true" data-testid="chat-attach-input" onChange={pick} />
      <button
        type="button"
        data-testid="chat-attach-btn"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        aria-label="Attach a file or photo (PDF, DOCX, TXT, PNG, JPG; up to 10 MB)"
        title="Attach a file or photo"
        className="h-10 w-10 flex-shrink-0 inline-flex items-center justify-center rounded-md border border-slate-300 text-[#0A2A5E] hover:bg-[#FDECD0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F5A623] disabled:opacity-50 transition-colors"
      >
        <Paperclip className="w-4 h-4" />
      </button>
    </>
  );
}

function statusText(status, progress, error) {
  if (status === "uploading") return `Uploading… ${progress}%`;
  if (status === "analyzing") return "Analyzing with your question…";
  if (status === "done") return "Attached";
  if (status === "error") return error || "Upload failed";
  return "Ready to send — not uploaded yet";
}

function StatusIcon({ status }) {
  if (status === "uploading" || status === "analyzing") return <Loader2 className="w-3 h-3 animate-spin" />;
  if (status === "done") return <CheckCircle2 className="w-3 h-3 text-emerald-600" />;
  if (status === "error") return <AlertCircle className="w-3 h-3 text-rose-600" />;
  return null;
}

function FilePreview({ file, thumb, isImage }) {
  if (isImage && thumb) return <img src={thumb} alt={`Preview of ${file.name}`} data-testid="chat-attachment-thumb" className="w-12 h-12 rounded-lg object-cover border border-slate-200 flex-shrink-0" />;
  const Icon = isImage ? ImageIcon : FileText;
  return <div className="w-12 h-12 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0 text-[#0A2A5E]"><Icon className="w-5 h-5" /></div>;
}

// Pending attachment chip shown before sending, with progress/success/error states
export function AttachmentChip({ file, status, progress, error, onRemove, disabled }) {
  const [thumb, setThumb] = useState(null);
  const isImage = file.type.startsWith("image/");
  useEffect(() => {
    if (!isImage) return undefined;
    const url = URL.createObjectURL(file);
    setThumb(url);
    return () => URL.revokeObjectURL(url);
  }, [file, isImage]);
  return (
    <div data-testid="chat-attachment-chip" role="group" aria-label={`Attachment ${file.name}`} className={`flex items-center gap-3 rounded-xl border px-3 py-2 text-sm ${status === "error" ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-[#F5F9FE]"}`}>
      <FilePreview file={file} thumb={thumb} isImage={isImage} />
      <div className="min-w-0 flex-1">
        <div data-testid="chat-attachment-name" className="font-semibold text-[#0A2A5E] truncate">{file.name}</div>
        <div className="text-[11px] text-slate-500 flex items-center gap-1.5" aria-live="polite">
          <StatusIcon status={status} />
          <span data-testid="chat-attachment-status">{formatBytes(file.size)} · {statusText(status, progress, error)}</span>
        </div>
        {status === "uploading" && <div className="mt-1 h-1 rounded bg-slate-200 overflow-hidden"><div className="h-full bg-[#F5A623] transition-[width]" style={{ width: `${progress}%` }} /></div>}
      </div>
      <button type="button" data-testid="chat-attachment-remove" onClick={onRemove} disabled={disabled} aria-label={`Remove attachment ${file.name}`}
        className="p-1.5 rounded-full text-slate-500 hover:text-rose-700 hover:bg-rose-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F5A623] disabled:opacity-40">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// Small badge used inside sent user messages and assistant replies
export function AttachmentBadge({ attachment, light }) {
  if (!attachment) return null;
  return (
    <div data-testid="message-attachment" className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${light ? "bg-white/15 text-white" : "bg-white border border-[#FBD38D] text-[#7C3F00]"}`}>
      {attachment.kind === "image" ? <ImageIcon className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
      <span className="truncate max-w-[220px]">{attachment.filename || attachment.name}</span>
      {attachment.size ? <span className="opacity-70">· {formatBytes(attachment.size)}</span> : null}
    </div>
  );
}
